<template>
	<Fullscreen ref="fullscreen" @change="fullscreenChange" class="full-screen" id="full-screen">
		<md-app class="full-screen" id="app">
			<md-app-content class="full-screen md-app-content">
				
				<!-- Situation Gallery	-->
				<transition name="slideDown">
					<SituationGallery v-show="situationGalleryVisible"/>
				</transition>

				<!-- Navigation Bar -->
				<Navigation 
					:mapVisible="mapVisible" :fullScreen="fullScreen" :loginDialogueVisible="loginDialogueVisible" :gyrosSupported="gyrosSupported"
					:gyrosActive="gyrosActive" :bugReportVisible="bugReportVisible" :situationGalleryVisible="situationGalleryVisible" :sharingVisible="sharingVisible" 
					:loggedIn="loggedIn" :comVisible="comVisible" :localUserIsModerator="localUserIsModerator" :tutorialVisible="tutorialVisible"
				/>

				<!-- Rendering -->
				<Flux360/>

				<!-- Loadingscreen between Situations -->
				<transition name="fadeLoadingscreen">
					<Loadingscreen v-show="loadingscreenVisible"/>
				</transition>

				<LanguageSelection/>

				<!-- Website Dialogue -->				
				<WebsiteDialogue/>

				<!-- Moderator Menu -->		
				<transition name="slideDown">		
					<ModeratorMenu v-show="moderatorMenueVisible"/>
				</transition>
				
				<!-- ForcedDisconnect Dialogue -->				
				<ForcedDisconnect/>

				<!-- Wait for Moderator Dialogue -->				
				<WaitForModeratorDialogue/>

				<!-- Preview of Transition on hover over -->
				<TransitionPreview/>

				<!-- 3D Model viewer -->
				<Viewer3D/>

				<!-- Video Fullscreen Dialogue -->				
				<VideoFullscreen/>

				<!-- BugReport Dialogue -->
				<transition name="fade">
					<BugReportDialogue v-if="bugReportVisible"/>
				</transition>

				<transition name="fade">
					<TutorialDialogue v-if="tutorialVisible"/>
				</transition>

				<!-- Login Dialogue -->
				<transition name="fade">
					<LoginDialogue :toggleLogin="this.toggleLogin" :loginDialogueVisible="loginDialogueVisible" v-if="loginDialogueVisible"/>
				</transition>

				<!-- Map Dialogue-->
				<transition name="fade">
					<MapDialogue v-if="enableMap" v-show="mapVisible"/>
				</transition>

				<!-- Share Dialogue -->
				<transition name="fade">
					<ShareDialogue v-if="sharingVisible"/> <!-- v-if because listening to url changes not working currently -->
				</transition>
				
				<!-- Screen Sharing Panel -->
				<transition name="fade">
					<ScreenSharingPanel v-if="screenSharingVisible"/>
				</transition>

				<!-- Media Controls -->
				<transition name="fade">
					<MediaController v-show="enablePermanentPanoramaVideoControls ? isVideoPanorama: mediaControlsVisible"/>			
				</transition>

				<!-- Flux360 Watermark -->
				<Watermark v-if="enableWatermark"/>

			</md-app-content>

			<!-- Sidebar -->
			<md-app-drawer :md-active="comVisible" v-if="isOnboardingComplete" md-right md-persistent="full">
				<Sidebar/>
			</md-app-drawer>

		</md-app>
	</Fullscreen>	
</template>


<script>
import {mapGetters,mapState,mapMutations} from 'vuex'
import {Prefab} from "@/store/flux360/PrefabDataTypes";

import Fullscreen from "vue-fullscreen/src/component.vue"

// Components
import Flux360 from "@/components/flux360/Flux360.vue";
import MapDialogue from "@/components/flux360/EmbeddedDialogues/MapDialogue/MapDialogue.vue";
import MediaController from "@/components/flux360/MediaController.vue";
import WaitForModeratorDialogue from "@/components/flux360/EmbeddedDialogues/WaitForModeratorDialogue.vue";
import Watermark from '@/components/flux360/Watermark.vue';
import Navigation from '@/components/flux360/Navigation.vue';
import ShareDialogue from '@/components/flux360/EmbeddedDialogues/ShareDialogue.vue';
import ModeratorMenu from '@/components/flux360/ModeratorMenu.vue';
import ScreenSharingPanel from '@/components/flux360/EmbeddedDialogues/ScreenSharingPanel.vue';
import LoginDialogue from "@/components/flux360/LoginComponents/LoginDialogue.vue";
import WebsiteDialogue from "@/components/flux360/EmbeddedDialogues/WebsiteDialogue.vue";
import Viewer3D from "@/components/flux360/EmbeddedDialogues/Viewer3D.vue";
import ForcedDisconnect from "@/components/flux360/EmbeddedDialogues/ForcedDisconnect.vue";
import BugReportDialogue from "@/components/flux360/EmbeddedDialogues/BugReportDialogue.vue";
import DisclaimerDialogue from "@/components/flux360/EmbeddedDialogues/DisclaimerDialogue.vue";
import Loadingscreen from "@/components/flux360/Loadingscreen.vue";
import LanguageSelection from "@/components/flux360/LanguageSelection.vue";
import TransitionPreview from "@/components/flux360/TransitionPreview";
import Sidebar from "@/components/flux360/SidebarComponents/Sidebar.vue";
import VideoFullscreen from "@/components/flux360/EmbeddedDialogues/VideoFullscreen.vue";
import SituationGallery from "@/components/flux360/EmbeddedDialogues/SituationGallery.vue";
import TutorialDialogue from "@/components/flux360/EmbeddedDialogues/TutorialDialogue.vue";

export default {
	components: {
		Fullscreen, Flux360, MapDialogue, MediaController, Sidebar, TransitionPreview, LanguageSelection, Navigation, Watermark, ShareDialogue, ForcedDisconnect, SituationGallery, TutorialDialogue,
		ScreenSharingPanel, LoginDialogue, WebsiteDialogue, DisclaimerDialogue, Loadingscreen, BugReportDialogue, Viewer3D, VideoFullscreen, WaitForModeratorDialogue, ModeratorMenu
	},
	data: () => ({
		publicPath: process.env.BASE_URL,
		comVisible: false,
		fullScreen: false,
		sharingVisible: false,
		messageVisible: false,
		situationGalleryVisible: false,
		infoVisible: false,
		mediaControlsVisible: false,
		mapVisible: false,
		screenSharingVisible: false,
		loginDialogueVisible: false,   
		loggedIn: false,
		bugReportVisible: false,
		gyrosActive: false,
		gyrosSupported: false,
		moderatorMenueVisible: false,
		tutorialVisible: true,
	}),
   	computed: {
		// ...mapState(['vrMode']),
		...mapState({
			openTokStreams: (state) => state.session.openTokStreams,
		}),
    	...mapGetters([
			'currentSituation','numberOfChatMessages', 'localUser', 'currentPrefabs','enableMap','enablePermanentPanoramaVideoControls',"enableWatermark",
			'primaryColor','secondaryColor',"fontFamily",'isDialogueOpen','loadingscreenVisible','favIcon','websiteTitle',"enableCommunication","sessionID"
		]),
      	...mapState({
			isOnboardingComplete: (state) => state.session.isOnboardingComplete,
		}),

		// checks if currentSituation contains panorama video and if so checks if neccessary video data is loaded 
		// needed to display mediaController UI properly
		isVideoPanorama(){
			//Check if currentSituation contains panorama video if not return false
			if(!(this.currentSituation && this.currentSituation.getType() == Prefab.PrefabType.SituationVideo))
				return false;
			
			//return true when neccessary panorama video data is loaded (duration)
			return this.currentSituation.runtimeState.duration > 0;
		},
		localUserIsModerator() {
			return this.localUser && this.localUser.getRole() === "moderator"
		},
		screenCastsAvailable() {
			return this.openTokStreams.find( x => x.videoType === 'screen');
		},
	},
	watch: {
		screenCastsAvailable(){
			this.toggleScreenSharing();	
		},
		enableCommunication(){
			if(this.sessionID && this.enableCommunication)
				this.toggleLoginDialogue();
		},
		primaryColor(){
			document.documentElement.style.setProperty('--primaryColor', this.primaryColor);
		},
		secondaryColor(){
			document.documentElement.style.setProperty('--secondaryColor', this.secondaryColor);
		},
		fontFamily(){
			document.documentElement.style.setProperty('--primaryFont', this.fontFamily);
		},
		favIcon(){
			document.querySelector("link[rel~='icon']").href = this.publicPath+'/media/'+this.favIcon;
		},
		websiteTitle(){
			document.title = this.websiteTitle;
		},
	},
	methods: {
		...mapMutations(["setDialogueOpen"]),
		toggleLoginDialogue(){
			this.loginDialogueVisible = !this.loginDialogueVisible;	
			// this.setDialogueOpen(this.loginDialogueVisible);
			if(this.loginDialogueVisible){
				this.situationGalleryVisible = false;
				this.mediaControlsVisible = false;
				this.sharingVisible = false;
				this.mapVisible = false;
				this.bugReportVisible = false;
				this.tutorialVisible = false;
			}						
		},
		toggleMediaControls() {
			this.mediaControlsVisible = !this.mediaControlsVisible;
			this.setDialogueOpen(false);
			if(this.mediaControlsVisible){
				this.situationGalleryVisible = false;
				this.sharingVisible = false;
				this.mapVisible = false;
				this.bugReportVisible = false;
				this.tutorialVisible = false;
			}
		},
		toggleGallery() {
			this.situationGalleryVisible = !this.situationGalleryVisible;
			this.setDialogueOpen(false);
			if(this.situationGalleryVisible){
				this.mediaControlsVisible = false;
				this.sharingVisible = false;
				this.mapVisible = false;
				this.bugReportVisible = false;
				this.tutorialVisible = false;
			}
		},
		toggleTutorialDialogue(){
			this.tutorialVisible = !this.tutorialVisible;
			// this.setDialogueOpen(this.tutorialVisible);	
			if(this.tutorialVisible){
				this.situationGalleryVisible = false;
				this.mediaControlsVisible = false;
				this.sharingVisible = false;
				this.mapVisible = false;
				this.bugReportVisible = false;
			}
		},
		toggleBugReport(){
			this.bugReportVisible = !this.bugReportVisible;
			this.setDialogueOpen(this.bugReportVisible);	
			if(this.bugReportVisible){
				this.situationGalleryVisible = false;
				this.mediaControlsVisible = false;
				this.sharingVisible = false;
				this.mapVisible = false;
				this.tutorialVisible = false;
			}
		},
		toggleSharing() {
			this.sharingVisible = !this.sharingVisible;
			this.setDialogueOpen(this.sharingVisible);	
			if(this.sharingVisible){
				this.situationGalleryVisible = false;
				this.mediaControlsVisible = false;
				this.mapVisible = false;
				this.bugReportVisible = false;
				this.tutorialVisible = false;
			}
		},
		toggleMap(){
			this.mapVisible = !this.mapVisible;
      		this.setDialogueOpen(this.mapVisible);
			this.mapVisible ? document.body.style.overscrollBehaviorY = "contain": document.body.style.overscrollBehaviorY = "auto";
			if(this.mapVisible){
				this.situationGalleryVisible = false;
				this.mediaControlsVisible = false;
				this.sharingVisible = false;
				this.bugReportVisible = false;
				this.tutorialVisible = false;
			}
		},
		toggleModeratorMenue(){
			this.moderatorMenueVisible = !this.moderatorMenueVisible;
		},
		toggleScreenSharing() {
			this.screenSharingVisible = !this.screenSharingVisible;
			if( this.screenSharingVisible ) {
				this.mediaControlsVisible = false;
				this.sharingVisible = false;
				this.bugReportVisible = false;
			}
		},		
		toggleFullScreen(){
			this.$refs['fullscreen'].toggle();
		},
		toggleCommunication(){
			this.comVisible = !this.comVisible
		},
		toggleVRMode() {
			this.$store.commit('setVRMode', !this.vrMode );
		},
		fullscreenChange(fullscreen) {
			this.fullScreen = fullscreen
		},
		toggleMediaButton(value){
			this.mediaButtonVisible = value;
		},
		onbeforeunload(e) {e.preventDefault(); e.returnValue = ' ';window.returnValue = ' ';return ' '},
		toggleLogin(){
			this.loggedIn = !this.loggedIn;
			this.toggleLoginDialogue();
			if(this.loggedIn)
				window.addEventListener("beforeunload",this.onbeforeunload);
			else
				window.removeEventListener("beforeunload",this.onbeforeunload);
		},
		toggleGyros(){
			this.gyrosActive = !this.gyrosActive;
			this.$store.commit('activateGyros',this.gyrosActive);
		},
	},
	created(){
		console.defaultLog = console.log.bind(console);
        console.logs = [];
        console.error = function(){
            console.defaultLog.apply(console, arguments);
            console.logs.push(Array.from(arguments));
		}

		//check if gyros supported
		if(window.DeviceOrientationEvent && 'ontouchstart' in window)
			this.gyrosSupported = true;
	},
	provide() {
		return {
			toggleMap: this.toggleMap,
			toggleSharing: this.toggleSharing,
			toggleGallery: this.toggleGallery,
			toggleInfo: this.toggleInfo,
			toggleLoginDialogue: this.toggleLoginDialogue,
			toggleLogin: this.toggleLogin,
			toggleCommunication: this.toggleCommunication,
			toggleBugReport: this.toggleBugReport,
			toggleScreenSharing: this.toggleScreenSharing,
			toggleMediaControls: this.toggleMediaControls,
			toggleFullScreen: this.toggleFullScreen,
			toggleGyros: this.toggleGyros,
			isVideoPanorama: this.isVideoPanorama,
			toggleModeratorMenue: this.toggleModeratorMenue,
			toggleTutorialDialogue: this.toggleTutorialDialogue,
		}
	},
}
</script>


<style>
:root{
	--primaryColor: #E76101;
  	--secondaryColor: #a34400;
	--primaryFont: Avenir;
	--md-theme-default-primary: var(--primaryColor);
}
::selection {
	background: var(--primaryColor)! important;
}
::-moz-selection { 
	background: var(--primaryColor) !important;
}
body {
  	font-family: var(--primaryFont), Helvetica, Arial, sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}
html, body {
	height: 100vh !important;
	width: 100vw !important;
}
.full-screen {
	height: 100% !important;
	width: 100% !important;
}
.md-app-content{
	padding: 0px;
	border-left: 0px;
	border-right: 0px;
	border-left: 0px;
	overflow: hidden;
	position: relative;
	top: 0px;
	left: 0px;
	padding: 0px;
}
.md-drawer.md-active{
	transform: translate3D(0,0,0);
	transition: height .5s cubic-bezier(.25,.8,.25,1), transform .4s cubic-bezier(.25,.8,.25,1);
    transition-timing-function: cubic-bezier(.25,.8,.25,1);
	box-shadow: -5px 0px 10px rgba(0, 0, 0, 0.35);
}
.material-design-icon{
	height: 2em;
	width: 2em;
}
.material-design-icon > .material-design-icon__svg{
	height: 2em;
	width: 2em;
	fill: white;
}


/*  SlideDown/SlideUp animation */
.slideDown-enter-active, .slideDown-leave-active {
	transition: max-height .2s ease-in-out;
}
.slideDown-enter, .slideDown-leave-to {
	max-height: 0px !important;
}

/*  Fade animation */
.fade-enter-active, .fade-leave-active {
	transition: opacity .4s;
}
.fade-enter, .fade-leave-to {
	opacity: 0;
}

/*  Fade animation for Loadingscreen*/
.fadeLoadingscreen-enter-active{
	transition: opacity .5s;
}
.fadeLoadingscreen-leave-active {
	transition: opacity .2s;
}
.fadeLoadingscreen-enter, .fadeLoadingscreen-leave-to {
	opacity: 0;
}
</style>